import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const { refreshUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/reset-password", { token, password });
      if (res.data.token) localStorage.setItem("ht_token", res.data.token);
      setDone(true);
      try { await refreshUser(); } catch { /* profile refresh is best-effort */ }
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      setError(formatApiError(err, "Could not reset your password"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 sm:py-24 text-center" data-testid="reset-no-token">
        <h1 className="font-display text-3xl font-bold text-zinc-950">This link isn't complete.</h1>
        <p className="mt-3 text-sm text-zinc-600">Request a fresh reset link and use the full URL from the email.</p>
        <Link to="/forgot-password" className="mt-6 inline-block bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md">Get a new link</Link>
      </main>
    );
  }

  return (
    <main className="max-w-md mx-auto px-4 py-16 sm:py-24" data-testid="reset-password-page">
      <Seo title="Choose a New Password | HiredToday" description="Set a new password for your HiredToday account." path="/reset-password" />
      {done ? (
        <div data-testid="reset-done">
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">Done. Password changed.</h1>
          <p className="mt-3 text-sm text-zinc-600">You are signed in. Taking you to your dashboard…</p>
        </div>
      ) : (
        <>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">Choose a new password.</h1>
          <p className="mt-2 text-sm text-zinc-500">At least 8 characters. Make it one you'll remember this time.</p>
          {error && (
            <div className="mt-6 border border-red-200 bg-red-50 text-red-800 text-sm rounded-md px-4 py-3" data-testid="reset-error">{error}</div>
          )}
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">New password</label>
              <input required type="password" minLength={8} data-testid="reset-password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Once more</label>
              <input required type="password" minLength={8} data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full h-12 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
              {loading ? "Saving…" : "Set new password"}
            </button>
          </form>
        </>
      )}
    </main>
  );
}
